import * as yup from 'yup';

/**
 * E41 · Behavioral activation — the both-ends input codecs of the tools' mutations (plan 03 §2.1 #3): the phone (before it saves), the
 * portal and — as the shield's inputRule — the API run the SAME schema. Shape only; the business rules (a rating 0-10, the hour that
 * has not happened, fifteen at most, a plan in the past, the barrier set, three supporters) are the decider's, where they are binding
 * and answer with the board's code in the person's language. `subject` is ABSENT from the client's own commands on purpose (the
 * caller is the subject); `performer` is never on the wire.
 *
 * The act's id is MINTED ON THE DEVICE (UUIDv7): a retried request is the store's duplicate and records nothing.
 */
const uuidV7 = yup
  .string()
  .trim()
  .matches(
    /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
  )
  .required();

const userRef = yup
  .string()
  .trim()
  .matches(/^users\/[A-Za-z0-9_-]{1,64}$/)
  .required();

const slug = yup.string().trim().min(1).max(64).required();
// an empty title / note is the decider's own refusal (BA_LOG_EMPTY · BA_PLAN_EMPTY), in the person's words
const words = (max: number) => yup.string().trim().max(max).required();
const optionalWords = (max: number) => yup.string().trim().max(max).nullable();

export const publishActivityLibraryInputSchema = yup.object({
  libraryId: slug,
  label: yup.string().trim().min(1).max(200).required(),
  ideas: yup
    .array()
    .of(
      yup
        .object({
          ideaId: slug,
          area: slug,
          // a missing line is the decider's LIBRARY_TRANSLATION_MISSING (in the member's words), not a generic "required"
          en: yup.string().max(300).defined(),
          ru: yup.string().max(300).defined(),
          difficulty: yup.number().integer().required(),
          values: yup.array().of(slug).max(10).nullable(),
        })
        .required(),
    )
    .max(500)
    .required(),
  locales: yup
    .array()
    .of(yup.string().trim().min(2).max(8).required())
    .min(1)
    .max(5)
    .required(),
  citation: yup.string().trim().min(1).max(1000).required(),
});

export const logActivityInputSchema = yup.object({
  activityLogId: uuidV7,
  startAt: yup.date().required(),
  endAt: yup.date().required(),
  title: words(300),
  enjoyment: yup.number().integer().required(),
  importance: yup.number().integer().required(),
  valueActivityId: yup.string().trim().max(64).nullable(),
  source: yup
    .string()
    .trim()
    .oneOf(['manual', 'suggestedFromHealth'])
    .nullable(),
});

export const removeActivityLogInputSchema = yup.object({
  activityLogId: uuidV7,
});

export const rankActivitiesInputSchema = yup.object({
  rankingId: uuidV7,
  ranking: yup
    .array()
    .of(
      yup
        .object({
          valueActivityId: slug,
          rank: yup.number().integer().required(),
        })
        .required(),
    )
    .max(40)
    .required(),
});

const planFields = {
  valueActivityId: yup.string().trim().max(64).nullable(),
  title: words(300),
  plannedFor: yup.date().required(),
  durationMinutes: yup.number().integer().min(0).max(1440).nullable(),
  predictedEnjoyment: yup.number().integer().required(),
  predictedImportance: yup.number().integer().required(),
  certainty: yup.number().integer().nullable(),
  replanOf: yup.string().trim().max(64).nullable(),
};

// the phone mints the plan's id (a retried request is the store's duplicate); in session the server does
export const planActivityInputSchema = yup.object({
  plannedActivityId: uuidV7,
  ...planFields,
});

export const planActivityInSessionInputSchema = yup.object({
  subject: userRef,
  ...planFields,
});

export const completePlannedActivityInputSchema = yup.object({
  plannedActivityId: uuidV7,
  enjoyment: yup.number().integer().required(),
  importance: yup.number().integer().required(),
  note: optionalWords(1000),
  timeZone: yup.string().trim().max(64).nullable(),
});

export const missPlannedActivityInputSchema = yup.object({
  plannedActivityId: uuidV7,
  barrier: slug,
  note: optionalWords(1000),
});

export const makeSupportContractInputSchema = yup.object({
  contractId: uuidV7,
  valueActivityId: slug,
  supporters: yup
    .array()
    .of(
      yup
        .object({
          name: yup.string().trim().max(120).defined(),
          howTheyHelp: yup.string().trim().max(500).defined(),
          contact: yup.string().trim().max(120).nullable(),
        })
        .required(),
    )
    .max(10)
    .required(),
  askBy: yup.date().nullable(),
});

/** The mutation ARGUMENTS — what the shield's inputRules validate. */
const args = <T extends yup.AnyObjectSchema>(input: T) =>
  yup.object({ input: input.required() });

export const publishActivityLibraryArgsSchema = args(
  publishActivityLibraryInputSchema,
);
export const logActivityArgsSchema = args(logActivityInputSchema);
export const removeActivityLogArgsSchema = args(removeActivityLogInputSchema);
export const rankActivitiesArgsSchema = args(rankActivitiesInputSchema);
export const planActivityArgsSchema = args(planActivityInputSchema);
export const planActivityInSessionArgsSchema = args(
  planActivityInSessionInputSchema,
);
export const completePlannedActivityArgsSchema = args(
  completePlannedActivityInputSchema,
);
export const missPlannedActivityArgsSchema = args(
  missPlannedActivityInputSchema,
);
export const makeSupportContractArgsSchema = args(
  makeSupportContractInputSchema,
);

export const behavioralActivationValidators = {
  publishActivityLibraryInputSchema,
  logActivityInputSchema,
  removeActivityLogInputSchema,
  rankActivitiesInputSchema,
  planActivityInputSchema,
  planActivityInSessionInputSchema,
  completePlannedActivityInputSchema,
  missPlannedActivityInputSchema,
  makeSupportContractInputSchema,
  publishActivityLibraryArgsSchema,
  logActivityArgsSchema,
  removeActivityLogArgsSchema,
  rankActivitiesArgsSchema,
  planActivityArgsSchema,
  planActivityInSessionArgsSchema,
  completePlannedActivityArgsSchema,
  missPlannedActivityArgsSchema,
  makeSupportContractArgsSchema,
};
